import { action, makeObservable, observable, toJS } from 'mobx';

import type { TDictObject } from 'src/api/directories/types';
import { hasValue } from 'src/shared/utils/common';
import { equalsDeep } from 'src/shared/utils/equals-deep';
import type { EventBus } from 'src/shared/utils/event-bus';
import { isObjectWithKeys } from 'src/shared/utils/is-object-with-keys';
import { parsePrimitivesFromString } from 'src/shared/utils/parse-primitives-from-string';
import type { RootStore } from 'src/store';
import type { Directories } from 'src/store/directories/directories.store';

import type { TGeologicalLoadData } from '../../geological-load-tab/types';
import type { IFormEvents } from '../form.entity';
import { RigOperationSection } from '../rig-operations/rig-operation-section.entity';
import { RigOperationStage } from '../rig-operations/rig-operation-stage.entity';
import { RigOperation } from '../rig-operations/rig-operation.entity';

import { GeoLoadRigOperation } from './geo-load-rig-operation.entity';
import { GeoLoadSection } from './geo-load-section.entity';
import { GeoLoadStage } from './geo-load-stage.entity';
import { GeologicalLoadValidation } from './geological-load-validation';
import { GeologicalLoad } from './geological-load.entity';
import {
  getExtractionVolume,
  getCoreSamplingDayAmount,
  getTestingFormationWorkDayAmount,
  getDaysAmountOfDataInterpretationOperation,
  isGeoLoadField,
  getStageSlug,
} from './utils';

export type TGeologicalLoadKeys = keyof TGeologicalLoadData;
type TValueChangePayload = {
  fieldId: string;
  value: unknown;
  oldValue: unknown;
  geoLoadRigOperation?: GeoLoadRigOperation;
  geoLoadStage?: GeoLoadStage;
  geoLoadSection?: GeoLoadSection;
};

export const GOPLAN_GEOLOGICAL_TASK_PREFIX = 'GOplan_GeologicalTask';
export const GEOLOAD_PROPERTIE_NAMES: TGeologicalLoadKeys[] = [
  'coreSelection',
  'testingFormationWork',
  'dataInterpretation',
  'coreSamplingIntervalId',
  'testingFormationWorkAmount',
  'numberOfDataInterpretationOperations',
  'extractionVolume',
  'coreSamplingDayAmount',
  'testingFormationWorkDayAmount',
  'daysAmountOfDataInterpretationOperation',
];
export const GEOTASK_GEOLOAD_PROPERTIE_NAMES = GEOLOAD_PROPERTIE_NAMES.map(
  (name) => `${GOPLAN_GEOLOGICAL_TASK_PREFIX}.${name}`,
);

export class GeologicalLoadService {
  private readonly eventBus: EventBus<IFormEvents>;
  private readonly rootStore: RootStore;
  private readonly directories: Directories;
  private readonly initialValues: Record<string, unknown> | null;
  readonly validation: GeologicalLoadValidation;

  @observable geotaskGeoLoad: GeologicalLoad;
  @observable geoLoadRigOperations: GeoLoadRigOperation[] = [];
  geoLoadRigOperationsMap = observable.map<number, GeoLoadRigOperation>();
  geoLoadRigOperationsByRigOperationIdMap = observable.map<number, GeoLoadRigOperation>();

  constructor(rootStore: RootStore, eventBus: EventBus<IFormEvents>, initialValues: Record<string, unknown> | null) {
    this.eventBus = eventBus;
    this.rootStore = rootStore;
    this.directories = rootStore.directories;
    this.initialValues = initialValues ?? null;
    this.geotaskGeoLoad = new GeologicalLoad(this.getInitialGeoTaskGeoLoad());
    this.validation = new GeologicalLoadValidation();

    makeObservable(this);
  }

  private getInitialGeoTaskGeoLoad(): TGeologicalLoadData {
    if (!this.initialValues) {
      return {};
    }

    const initialGeoTaskData: TGeologicalLoadData = {};

    for (const name of GEOTASK_GEOLOAD_PROPERTIE_NAMES) {
      const value = (() => {
        const rawValue = this.initialValues[name];

        if (!hasValue(rawValue)) {
          return null;
        }

        return parsePrimitivesFromString(rawValue as string);
      })();

      if (hasValue(value)) {
        const key = name.split('.')[1] as keyof TGeologicalLoadData;

        // TODO: улучшить типизацию
        initialGeoTaskData[key] = value as any;
      }
    }

    return initialGeoTaskData;
  }

  private getInitialGeoLoad(
    entity: RigOperation | RigOperationStage | RigOperationSection,
  ): TGeologicalLoadData | null {
    const source = (() => {
      if (entity instanceof RigOperation) {
        return entity.rawRigOperation;
      }

      if (entity instanceof RigOperationStage) {
        return entity.initialRawStage;
      }

      if (entity instanceof RigOperationSection) {
        return entity.initialRawSection;
      }
    })();

    if (!source) {
      return null;
    }

    const initialGeoTaskData: TGeologicalLoadData = {};

    for (const name of GEOLOAD_PROPERTIE_NAMES) {
      const value = (() => {
        const rawValue = source[name];

        if (!hasValue(rawValue)) {
          return null;
        }

        return parsePrimitivesFromString(rawValue as string);
      })();

      if (hasValue(value)) {
        // TODO: улучшить типизацию
        initialGeoTaskData[name] = value as any;
      }
    }

    if (!isObjectWithKeys(initialGeoTaskData)) {
      return null;
    }

    return initialGeoTaskData;
  }

  @action.bound
  private clearGeotaskGeoLoad(): void {
    this.geotaskGeoLoad.clearData();
  }

  @action.bound
  private onCoreSamplingIntervalIdChange({
    value,
    geoLoadRigOperation,
    geoLoadStage,
    geoLoadSection,
  }: Omit<TValueChangePayload, 'fieldId' | 'value' | 'oldValue'> & {
    fieldId: 'coreSamplingIntervalId';
    value: number[];
  }): void {
    const coreSamplingIntervalDirectory = this.directories.getObject('Common_CoreSamplingInterval');

    if (!coreSamplingIntervalDirectory) {
      console.error('cannot find coreSamplingIntervalDirectory');
      return;
    }

    const coreSamplingIntervalDirectoryMap = new Map<number, TDictObject>(
      coreSamplingIntervalDirectory.map((d) => [d.id as number, d]),
    );

    const extractionVolume = getExtractionVolume(coreSamplingIntervalDirectoryMap, value);
    const coreSamplingDayAmount = getCoreSamplingDayAmount(coreSamplingIntervalDirectoryMap, value);

    this.onValueChange('extractionVolume', extractionVolume, { geoLoadRigOperation, geoLoadStage, geoLoadSection });
    this.onValueChange('coreSamplingDayAmount', coreSamplingDayAmount, {
      geoLoadRigOperation,
      geoLoadStage,
      geoLoadSection,
    });
  }

  @action.bound
  private onTestingFormationWorkAmountChange({
    value,
    geoLoadRigOperation,
    geoLoadStage,
    geoLoadSection,
  }: Omit<TValueChangePayload, 'fieldId' | 'value' | 'oldValue'> & {
    fieldId: 'testingFormationWorkAmount';
    value: number | null;
  }): void {
    // TODO: обработать архивные значения
    const geologicalLoadDirectory = this.directories.getObject('Common_GeologicalLoad', true)?.[0];

    if (!geologicalLoadDirectory) {
      console.error('cannot find geologicalLoadDirectory');
      return;
    }

    const testingFormationWorkDayAmount = getTestingFormationWorkDayAmount(geologicalLoadDirectory, value);

    this.onValueChange('testingFormationWorkDayAmount', testingFormationWorkDayAmount, {
      geoLoadRigOperation,
      geoLoadStage,
      geoLoadSection,
    });
  }

  @action.bound
  private onNumberOfDataInterpretationOperationsChange({
    value,
    geoLoadRigOperation,
    geoLoadStage,
    geoLoadSection,
  }: Omit<TValueChangePayload, 'fieldId' | 'value' | 'oldValue'> & {
    fieldId: 'numberOfDataInterpretationOperations';
    value: number | null;
  }): void {
    // TODO: обработать архивные значения
    const geologicalLoadDirectory = this.directories.getObject('Common_GeologicalLoad', true)?.[0];

    if (!geologicalLoadDirectory) {
      console.error('cannot find geologicalLoadDirectory');
      return;
    }

    const daysAmountOfDataInterpretationOperation = getDaysAmountOfDataInterpretationOperation(
      geologicalLoadDirectory,
      value,
    );

    this.onValueChange('daysAmountOfDataInterpretationOperation', daysAmountOfDataInterpretationOperation, {
      geoLoadRigOperation,
      geoLoadStage,
      geoLoadSection,
    });
  }

  @action.bound
  private onGeoLoadFieldValueChange({
    fieldId,
    value,
    geoLoadRigOperation,
    geoLoadStage,
    geoLoadSection,
  }: {
    fieldId: string;
    value: unknown;
    geoLoadRigOperation?: GeoLoadRigOperation;
    geoLoadStage?: GeoLoadStage;
    geoLoadSection?: GeoLoadSection;
  }) {
    if (!isGeoLoadField(fieldId)) {
      console.error('wrong fieldId', fieldId);
      return;
    }

    const geoLoad = (() => {
      if (geoLoadSection) {
        return geoLoadSection.geologicalLoad;
      }

      if (geoLoadStage) {
        return geoLoadStage.geologicalLoad;
      }

      if (geoLoadRigOperation) {
        return geoLoadRigOperation.geologicalLoad;
      }

      return this.geotaskGeoLoad;
    })();

    if (!geoLoad) {
      console.error('geoLoad is not presented');
      return;
    }

    const oldValue = geoLoad[fieldId];

    switch (fieldId) {
      case 'coreSamplingIntervalId': {
        geoLoad.removeError('coreSamplingIntervalId');

        if (!Array.isArray(value) || value.some((v) => typeof v !== 'number')) {
          console.error('coreSamplingIntervalId value is wrong', value);
          return;
        }

        if (equalsDeep(value, geoLoad.coreSamplingIntervalId)) {
          return;
        }

        geoLoad.setCoreSamplingIntervalId(value);

        this.onCoreSamplingIntervalIdChange({ fieldId, value, geoLoadRigOperation, geoLoadStage, geoLoadSection });

        break;
      }

      case 'testingFormationWorkAmount': {
        geoLoad.removeError('testingFormationWorkAmount');

        if (value !== null && typeof value !== 'number') {
          console.error('testingFormationWorkAmount value is wrong', value);
          return;
        }

        if (value === geoLoad.testingFormationWorkAmount) {
          return;
        }

        geoLoad.setTestingFormationWorkAmount(value);

        this.onTestingFormationWorkAmountChange({ fieldId, value, geoLoadRigOperation, geoLoadStage, geoLoadSection });

        break;
      }

      case 'numberOfDataInterpretationOperations': {
        geoLoad.removeError('numberOfDataInterpretationOperations');

        if (value !== null && typeof value !== 'number') {
          console.error('numberOfDataInterpretationOperations value is wrong', value);
          return;
        }

        if (value === geoLoad.numberOfDataInterpretationOperations) {
          return;
        }

        geoLoad.setNumberOfDataInterpretationOperations(value);

        this.onNumberOfDataInterpretationOperationsChange({
          fieldId,
          value,
          geoLoadRigOperation,
          geoLoadStage,
          geoLoadSection,
        });

        break;
      }

      case 'coreSelection': {
        geoLoad.removeError('coreSelection');

        if (value === geoLoad.coreSelection) {
          return;
        }

        geoLoad.setCoreSelection(!!value);

        if (!value) {
          this.onValueChange('coreSamplingIntervalId', [], { geoLoadRigOperation, geoLoadStage, geoLoadSection });
        }

        break;
      }
      case 'dataInterpretation': {
        geoLoad.removeError('dataInterpretation');

        if (value === geoLoad.dataInterpretation) {
          return;
        }

        geoLoad.setDataInterpretation(!!value);

        if (!value) {
          this.onValueChange('numberOfDataInterpretationOperations', null, {
            geoLoadRigOperation,
            geoLoadStage,
            geoLoadSection,
          });
        }

        break;
      }
      case 'testingFormationWork': {
        geoLoad.removeError('testingFormationWork');

        if (value === geoLoad.testingFormationWork) {
          return;
        }

        geoLoad.setTestingFormationWork(!!value);

        if (!value) {
          this.onValueChange('testingFormationWorkAmount', null, { geoLoadRigOperation, geoLoadStage, geoLoadSection });
        }

        break;
      }
      case 'extractionVolume': {
        geoLoad.removeError('extractionVolume');

        if (value !== null && typeof value !== 'number') {
          console.error('extractionVolume value is wrong', value);
          return;
        }

        if (value === geoLoad.extractionVolume) {
          return;
        }

        geoLoad.setExtractionVolume(value);

        break;
      }
      case 'coreSamplingDayAmount': {
        geoLoad.removeError('coreSamplingDayAmount');

        if (value !== null && typeof value !== 'number') {
          console.error('coreSamplingDayAmount value is wrong', value);
          return;
        }

        if (value === geoLoad.coreSamplingDayAmount) {
          return;
        }

        geoLoad.setCoreSamplingDayAmount(value);

        break;
      }
      case 'daysAmountOfDataInterpretationOperation': {
        geoLoad.removeError('daysAmountOfDataInterpretationOperation');

        if (value !== null && typeof value !== 'number') {
          console.error('daysAmountOfDataInterpretationOperation value is wrong', value);
          return;
        }

        if (value === geoLoad.daysAmountOfDataInterpretationOperation) {
          return;
        }

        geoLoad.setDaysAmountOfDataInterpretationOperation(value);

        break;
      }
      case 'testingFormationWorkDayAmount': {
        geoLoad.removeError('testingFormationWorkDayAmount');

        if (value !== null && typeof value !== 'number') {
          console.error('testingFormationWorkDayAmount value is wrong', value);
          return;
        }

        if (value === geoLoad.testingFormationWorkDayAmount) {
          return;
        }

        geoLoad.setTestingFormationWorkDayAmount(value);

        break;
      }
    }

    this.eventBus.publish('changeGeoLoadFieldValue', {
      fieldId,
      value,
      oldValue,
      geoLoadRigOperation,
      geoLoadStage,
      geoLoadSection,
    });
  }

  @action.bound
  private initilizeRigOperations(rigOperations: RigOperation[]): void {
    if (!rigOperations.length) {
      const geoTaskInitialGeologicalLoad = this.getInitialGeoTaskGeoLoad();

      this.geotaskGeoLoad = new GeologicalLoad(geoTaskInitialGeologicalLoad);
      return;
    }

    const geoLoadRigOperations: GeoLoadRigOperation[] = [];

    rigOperations.forEach((rigOperation) => {
      const initialGeoload = (() => {
        const rigOperationGeoLoad = this.getInitialGeoLoad(rigOperation);

        if (rigOperation.index > 0) {
          return rigOperationGeoLoad;
        }

        return rigOperationGeoLoad;
      })();

      const rigOperationGeoLoad = new GeoLoadRigOperation(rigOperation.entityId, initialGeoload);

      geoLoadRigOperations.push(rigOperationGeoLoad);
      this.geoLoadRigOperationsMap.set(rigOperationGeoLoad.entityId, rigOperationGeoLoad);
      this.geoLoadRigOperationsByRigOperationIdMap.set(rigOperation.entityId, rigOperationGeoLoad);

      this.initializeStages(rigOperationGeoLoad, rigOperation, rigOperation.stages);
    });

    this.geoLoadRigOperations = geoLoadRigOperations;
  }

  @action.bound
  private initializeStages(
    geoLoadRigOperation: GeoLoadRigOperation,
    rigOperation: RigOperation,
    stages: RigOperationStage[],
  ): void {
    const geoLoadStages: GeoLoadStage[] = [];

    for (const stage of stages) {
      if (!hasValue(stage.wellLifeCycleStageId)) {
        continue;
      }

      if (getStageSlug(this.directories, stage.wellLifeCycleStageId) !== 'drilling') {
        continue;
      }

      const initialGeoload = (() => {
        const stageGeoLoad = this.getInitialGeoLoad(stage);

        if (stageGeoLoad) {
          geoLoadRigOperation.geologicalLoad.clearData();

          return stageGeoLoad;
        }

        const rigOperationGeoLoad = toJS(geoLoadRigOperation.geologicalLoad.initialData);

        if (!rigOperationGeoLoad) {
          return null;
        }

        geoLoadRigOperation.geologicalLoad.clearData();

        return rigOperationGeoLoad;
      })();

      const geoLoadStage = new GeoLoadStage(rigOperation.entityId, stage.entityId, initialGeoload);

      geoLoadStages.push(geoLoadStage);

      this.initializeSections(geoLoadStage, rigOperation, stage, stage.sections);
    }

    geoLoadRigOperation.setStages(geoLoadStages);
  }

  @action.bound
  private initializeSections(
    geoLoadStage: GeoLoadStage,
    rigOperation: RigOperation,
    stage: RigOperationStage,
    sections: RigOperationSection[],
  ) {
    const geoLoadSections: GeoLoadSection[] = [];

    if (sections.length) {
      geoLoadStage.geologicalLoad.clearData();
    }

    for (const section of sections) {
      const initialGeoLoad = this.getInitialGeoLoad(section);

      const geoLoadSection = new GeoLoadSection(
        rigOperation.entityId,
        stage.entityId,
        section.entityId,
        initialGeoLoad,
      );

      geoLoadSections.push(geoLoadSection);
    }

    geoLoadStage.setSections(geoLoadSections);
  }

  @action.bound
  private onNewRigOperation({ rigOperation }: { rigOperation: RigOperation; index: number }): void {
    const initialGeoLoad = (() => {
      const isFirstRigOperation = rigOperation.index === 0;
      const data = (() => {
        if (isFirstRigOperation) {
          return this.getInitialGeoLoad(rigOperation) ?? toJS(this.geotaskGeoLoad?.initialData);
        }

        return this.getInitialGeoLoad(rigOperation);
      })();

      if (isFirstRigOperation) {
        this.clearGeotaskGeoLoad();
      }

      return data;
    })();

    const rigOperationGeoLoad = new GeoLoadRigOperation(rigOperation.entityId, initialGeoLoad);

    this.geoLoadRigOperations = [...this.geoLoadRigOperations, rigOperationGeoLoad];
    this.geoLoadRigOperationsMap.set(rigOperationGeoLoad.entityId, rigOperationGeoLoad);
    this.geoLoadRigOperationsByRigOperationIdMap.set(rigOperation.entityId, rigOperationGeoLoad);

    this.initializeStages(rigOperationGeoLoad, rigOperation, rigOperation.stages);
  }

  @action.bound
  private onNewStage(rigOperation: RigOperation, stage: RigOperationStage): void {
    const geoLoadRigOperation = this.geoLoadRigOperationsByRigOperationIdMap.get(rigOperation.entityId);

    if (!geoLoadRigOperation) {
      console.error('cannot find coresponding geoLoadRigOperation', rigOperation);
      return;
    }

    if (
      !hasValue(stage.wellLifeCycleStageId) ||
      getStageSlug(this.directories, stage.wellLifeCycleStageId) !== 'drilling'
    ) {
      return;
    }

    const initialGeoLoad = (() => {
      const data = this.getInitialGeoLoad(stage) ?? toJS(geoLoadRigOperation.geologicalLoad.initialData);

      geoLoadRigOperation.geologicalLoad.clearData();

      return data;
    })();

    const geoLoadStage = new GeoLoadStage(rigOperation.entityId, stage.entityId, initialGeoLoad);

    geoLoadRigOperation.setStages([...geoLoadRigOperation.stages, geoLoadStage]);

    this.initializeSections(geoLoadStage, rigOperation, stage, stage.sections);
  }

  @action.bound
  private onNewSection(rigOperation: RigOperation, stage: RigOperationStage, section: RigOperationSection): void {
    const geoLoadRigOperation = this.geoLoadRigOperationsByRigOperationIdMap.get(rigOperation.entityId);
    const geoLoadStage = geoLoadRigOperation?.stages.find(
      (geoLoadStage) => geoLoadStage.stageEntityId === stage.entityId,
    );

    if (!geoLoadStage) {
      console.error('cannot find corresponding geoLoadStage', rigOperation, stage, geoLoadRigOperation);
      return;
    }

    const initialGeoLoad = (() => {
      const data = this.getInitialGeoLoad(section);

      geoLoadStage.geologicalLoad.clearData();

      return data;
    })();

    const geoLoadSection = new GeoLoadSection(rigOperation.entityId, stage.entityId, section.entityId, initialGeoLoad);

    geoLoadStage.setSections([...geoLoadStage.sections, geoLoadSection]);
  }

  @action.bound
  private onDeleteRigOperation(rigOperation: RigOperation): void {
    const geoLoadRigOperation = this.geoLoadRigOperationsByRigOperationIdMap.get(rigOperation.entityId);

    if (!geoLoadRigOperation) {
      console.error('cannot find coresponding geoLoadRigOperation', rigOperation);
      return;
    }

    this.geoLoadRigOperationsByRigOperationIdMap.delete(rigOperation.entityId);
    this.geoLoadRigOperationsMap.delete(geoLoadRigOperation.entityId);

    this.geoLoadRigOperations = this.geoLoadRigOperations.filter((gl) => gl.entityId !== geoLoadRigOperation.entityId);
  }

  @action.bound
  private onDeleteStage(rigOperation: RigOperation, stage: RigOperationStage): void {
    const geoLoadRigOperation = this.geoLoadRigOperationsByRigOperationIdMap.get(rigOperation.entityId);

    if (!geoLoadRigOperation) {
      console.error('cannot find coresponding geoLoadRigOperation', rigOperation);
      return;
    }

    geoLoadRigOperation.setStages(geoLoadRigOperation.stages.filter((s) => s.stageEntityId !== stage.entityId));
  }

  @action.bound
  private onDeleteSection(rigOperation: RigOperation, stage: RigOperationStage, section: RigOperationSection): void {
    const geoLoadRigOperation = this.geoLoadRigOperationsByRigOperationIdMap.get(rigOperation.entityId);
    const geoLoadStage = geoLoadRigOperation?.stages.find((s) => s.stageEntityId === stage.entityId);

    if (!geoLoadStage) {
      console.error('cannot find coresponding geoLoadStage', rigOperation, stage, geoLoadRigOperation);
      return;
    }

    geoLoadStage.setSections(geoLoadStage.sections.filter((s) => s.sectionEntityId !== section.entityId));
  }

  @action.bound
  private onUpdateRigOperations(rigOperations: RigOperation[]): void {
    const newGeoLoadRigOperations: GeoLoadRigOperation[] = [];

    rigOperations.forEach((rigOperation) => {
      const existingGeoLoadRigOperation = this.geoLoadRigOperationsByRigOperationIdMap.get(rigOperation.entityId);

      if (!existingGeoLoadRigOperation) {
        const initialGeoLoad = this.getInitialGeoLoad(rigOperation);
        const rigOperationGeoLoad = new GeoLoadRigOperation(rigOperation.entityId, initialGeoLoad);

        this.geoLoadRigOperationsMap.set(rigOperationGeoLoad.entityId, rigOperationGeoLoad);
        this.geoLoadRigOperationsByRigOperationIdMap.set(rigOperation.entityId, rigOperationGeoLoad);

        this.initializeStages(rigOperationGeoLoad, rigOperation, rigOperation.stages);

        newGeoLoadRigOperations.push(rigOperationGeoLoad);
      } else {
        newGeoLoadRigOperations.push(existingGeoLoadRigOperation);
      }
    });

    const deletedGeoLoadRigOperations = this.geoLoadRigOperations.filter(
      (geoLoadRigOperation) => !newGeoLoadRigOperations.includes(geoLoadRigOperation),
    );

    deletedGeoLoadRigOperations.forEach((geoLoadRO) => {
      this.geoLoadRigOperationsByRigOperationIdMap.delete(geoLoadRO.rigOperationEntityId);
      this.geoLoadRigOperationsMap.delete(geoLoadRO.entityId);
    });

    this.geoLoadRigOperations = newGeoLoadRigOperations;
  }

  @action.bound
  private onUpdateStages(rigOperation: RigOperation, stages: RigOperationStage[]): void {
    const geoLoadRigOperation = this.geoLoadRigOperationsByRigOperationIdMap.get(rigOperation.entityId);

    if (!geoLoadRigOperation) {
      console.error('cannot find corresponding geoLoadRigOperation', rigOperation);
      return;
    }

    const newGeoLoadStages: GeoLoadStage[] = [];

    for (const stage of stages) {
      if (!hasValue(stage.wellLifeCycleStageId)) {
        continue;
      }

      if (getStageSlug(this.directories, stage.wellLifeCycleStageId) !== 'drilling') {
        continue;
      }

      const existingStage = geoLoadRigOperation.stages.find((s) => s.stageEntityId === stage.entityId);

      if (existingStage) {
        newGeoLoadStages.push(existingStage);
      } else {
        const initialGeoLoad = (() => {
          const stageGeoLoad = this.getInitialGeoLoad(stage);

          if (stageGeoLoad) {
            geoLoadRigOperation.geologicalLoad.clearData();

            return stageGeoLoad;
          }

          const rigOperationGeoLoad = toJS(geoLoadRigOperation.geologicalLoad.initialData);

          if (!rigOperationGeoLoad) {
            return null;
          }

          geoLoadRigOperation.geologicalLoad.clearData();

          return rigOperationGeoLoad;
        })();
        const stageGeoLoad = new GeoLoadStage(rigOperation.entityId, stage.entityId, initialGeoLoad);

        this.initializeSections(stageGeoLoad, rigOperation, stage, stage.sections);

        newGeoLoadStages.push(stageGeoLoad);
      }
    }

    geoLoadRigOperation.setStages(newGeoLoadStages);
  }

  @action.bound
  private onUpdateSections(
    rigOperation: RigOperation,
    stage: RigOperationStage,
    sections: RigOperationSection[],
  ): void {
    const geoLoadRigOperation = this.geoLoadRigOperationsByRigOperationIdMap.get(rigOperation.entityId);
    const geoLoadStage = geoLoadRigOperation?.stages.find((s) => s.stageEntityId === stage.entityId);

    if (!geoLoadStage) {
      console.error('cannot find corresponding geoLoadStage', rigOperation, stage, geoLoadRigOperation);
      return;
    }

    const newGeoLoadSections: GeoLoadSection[] = [];

    sections.forEach((section) => {
      const existingSection = geoLoadStage.sections.find((s) => s.sectionEntityId === section.entityId);

      if (existingSection) {
        newGeoLoadSections.push(existingSection);
      } else {
        const initialGeoLoad = this.getInitialGeoLoad(section);
        const sectionGeoLoad = new GeoLoadSection(
          rigOperation.entityId,
          stage.entityId,
          section.entityId,
          initialGeoLoad,
        );

        newGeoLoadSections.push(sectionGeoLoad);
      }
    });

    geoLoadStage.setSections(newGeoLoadSections);
  }

  @action.bound
  onValueChange(
    fieldId: string,
    value: unknown,
    {
      geoLoadRigOperation,
      geoLoadStage,
      geoLoadSection,
    }: { geoLoadRigOperation?: GeoLoadRigOperation; geoLoadStage?: GeoLoadStage; geoLoadSection?: GeoLoadSection },
  ): void {
    this.onGeoLoadFieldValueChange({
      fieldId,
      value,
      geoLoadRigOperation,
      geoLoadStage,
      geoLoadSection,
    });
  }

  init = (): VoidFunction => {
    const initializedRigOperationsDisposer = this.eventBus.subscribe(
      'initializedRigOperaionList',
      this.initilizeRigOperations,
    );

    const newRigOperationDisposer = this.eventBus.subscribe('newRigOperation', this.onNewRigOperation);
    const newStageDisposer = this.eventBus.subscribe('newStage', ({ rigOperation, stage }) =>
      this.onNewStage(rigOperation, stage),
    );
    const newSectionDisposer = this.eventBus.subscribe('newSection', ({ rigOperation, stage, section }) =>
      this.onNewSection(rigOperation, stage, section),
    );
    const deletedRigOperationDisposer = this.eventBus.subscribe('deleteRigOperation', this.onDeleteRigOperation);
    const deletedStageDisposer = this.eventBus.subscribe('deleteStage', ({ rigOperation, stage }) =>
      this.onDeleteStage(rigOperation, stage),
    );
    const deletedSectionDisposer = this.eventBus.subscribe('deleteSection', ({ rigOperation, stage, section }) =>
      this.onDeleteSection(rigOperation, stage, section),
    );
    const updatedRigOperationsDisposer = this.eventBus.subscribe('updatedRigOperaionList', this.onUpdateRigOperations);
    const updatedStageListDisposer = this.eventBus.subscribe('updatedStageList', ({ rigOperation, stages }) =>
      this.onUpdateStages(rigOperation, stages),
    );
    const updatedSectionsListDisposer = this.eventBus.subscribe(
      'updatedSectionsList',
      ({ rigOperation, stage, sections }) => this.onUpdateSections(rigOperation, stage, sections),
    );

    return () => {
      initializedRigOperationsDisposer();
      newRigOperationDisposer();
      newStageDisposer();
      newSectionDisposer();
      updatedRigOperationsDisposer();
      updatedStageListDisposer();
      updatedSectionsListDisposer();
      deletedRigOperationDisposer();
      deletedStageDisposer();
      deletedSectionDisposer();
    };
  };
}

declare module '../form.entity' {
  export interface IFormEvents {
    changeGeoLoadFieldValue: TValueChangePayload;
  }
}
